/**
 * Shared UI kit: buttons with a delayed press (press animation + click sound play before the
 * action fires), modal with blurred scrim, animated toggle, progress, dots, rich lesson text,
 * top bar, toast, sparkle background.
 */
import { useEffect, useRef, useState, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { create } from 'zustand';
import { parseRich } from '@sketchling/core';
import { play, type Sfx } from '../lib/sfx';
import { Icon, SparkGlyph, type IconName } from './icons';

const PRESS_DELAY_MS = 120;

type Variant = 'primary' | 'secondary' | 'paper' | 'ghost' | 'danger';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> {
  variant?: Variant;
  small?: boolean;
  block?: boolean;
  icon?: IconName;
  sfx?: Sfx | null;
  /** Fire immediately (no press delay), e.g. inside fast loops / text inputs. */
  instant?: boolean;
  onClick?: () => void;
}

export function Button({ variant = 'primary', small, block, icon, sfx = 'click', instant, onClick, className = '', children, disabled, ...rest }: ButtonProps) {
  const [pressed, setPressed] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => void (timer.current && clearTimeout(timer.current)), []);
  const handle = () => {
    if (disabled) {
      play('disabled');
      return;
    }
    // A press is already on its way: a second tap / ghost click must not fire the action twice.
    if (timer.current) return;
    if (sfx) play(sfx);
    if (instant || !onClick) return onClick?.();
    setPressed(true);
    timer.current = setTimeout(() => {
      timer.current = null;
      setPressed(false);
      onClick();
    }, PRESS_DELAY_MS);
  };
  return (
    <button
      type="button"
      className={`btn btn-${variant}${small ? ' btn-small' : ''}${block ? ' btn-block' : ''} ${className}`}
      data-pressed={pressed}
      aria-disabled={disabled || undefined}
      onClick={handle}
      {...rest}
    >
      {icon && <Icon name={icon} size={20} />}
      {children}
    </button>
  );
}

export function IconButton({
  icon,
  label,
  onClick,
  className = '',
  sfx = 'click',
  ...rest
}: { icon: IconName; label: string; onClick?: () => void; sfx?: Sfx | null } & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'>) {
  return (
    <button
      type="button"
      className={`icon-btn ${className}`}
      aria-label={label}
      title={label}
      onClick={() => {
        if (sfx) play(sfx);
        onClick?.();
      }}
      {...rest}
    >
      <Icon name={icon} />
    </button>
  );
}

export function Modal({
  open = true,
  onClose,
  children,
  wide,
  paper,
  label,
  testId,
}: {
  open?: boolean;
  onClose?: () => void;
  children: ReactNode;
  wide?: boolean;
  paper?: boolean;
  label?: string;
  testId?: string;
}) {
  useEffect(() => {
    if (!open || !onClose) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="scrim" onClick={(e) => e.target === e.currentTarget && onClose?.()}>
      <div className={`modal${wide ? ' wide' : ''}${paper ? ' paper on-paper' : ''}`} role="dialog" aria-modal="true" aria-label={label} data-testid={testId}>
        {onClose && <IconButton icon="close" label="Close" className="modal-close" onClick={onClose} />}
        {children}
      </div>
    </div>
  );
}

export function Toggle({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      className="toggle"
      onClick={() => {
        play('slider');
        onChange(!checked);
      }}
    />
  );
}

export function ProgressBar({ value, tone }: { value: number; tone?: 'gold' | 'success' }) {
  return (
    <div className={`progress${tone ? ' ' + tone : ''}`} role="progressbar" aria-valuenow={Math.round(value * 100)} aria-valuemin={0} aria-valuemax={100}>
      <i style={{ width: `${Math.max(0, Math.min(1, value)) * 100}%` }} />
    </div>
  );
}

export function Dots({ count, index }: { count: number; index: number }) {
  return (
    <div className="dots" aria-hidden>
      {Array.from({ length: count }, (_, i) => (
        <i key={i} className={i === index ? 'on' : ''} />
      ))}
    </div>
  );
}

/** Lesson copy with `*emphasis*{token}` spans. */
export function RichText({ text, as: Tag = 'span', className }: { text: string; as?: 'span' | 'p' | 'h2' | 'h3'; className?: string }) {
  return (
    <Tag className={className}>
      {parseRich(text).map((s, i) => (s.token ? <b key={i} className={`em-${s.token}`}>{s.text}</b> : <span key={i}>{s.text}</span>))}
    </Tag>
  );
}

export function SparkCounter({ value, onClick }: { value: number; onClick?: () => void }) {
  return (
    <button type="button" className="chip spark-counter" onClick={onClick} aria-label={`${value} sparks`} data-testid="spark-counter">
      <SparkGlyph size={18} />
      <b>{value}</b>
    </button>
  );
}

export function TopBar({ title, onBack, right, children }: { title?: ReactNode; onBack?: () => void; right?: ReactNode; children?: ReactNode }) {
  return (
    <header className="topbar">
      {onBack && <IconButton icon="back" label="Back" onClick={onBack} />}
      {title !== undefined && <h2>{title}</h2>}
      {children}
      {right}
    </header>
  );
}

/** Sparse five-point star dots over the night background (decorative). */
export function StarsLayer({ count = 14, seed = 3 }: { count?: number; seed?: number }) {
  let s = seed;
  const rnd = () => ((s = (s * 16807) % 2147483647) - 1) / 2147483646;
  const stars = Array.from({ length: count }, () => ({ x: rnd() * 100, y: rnd() * 100, r: 0.5 + rnd() * 0.9, d: rnd() * 4 }));
  return (
    <svg className="stars-layer" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden>
      {stars.map((st, i) => (
        <circle key={i} cx={st.x} cy={st.y} r={st.r * 0.35} fill="#FFFBF4" style={{ animation: `twinkle ${3 + st.d}s ease-in-out ${st.d}s infinite`, transformOrigin: `${st.x}px ${st.y}px` }} />
      ))}
    </svg>
  );
}

/* ---------------------------------- Toast ---------------------------------- */

const useToastStore = create<{ msg: string | null; key: number; show: (m: string) => void; hide: () => void }>((set) => ({
  msg: null,
  key: 0,
  show: (msg) => set((s) => ({ msg, key: s.key + 1 })),
  hide: () => set({ msg: null }),
}));

export const toast = (m: string) => useToastStore.getState().show(m);

export function ToastHost() {
  const msg = useToastStore((s) => s.msg);
  const key = useToastStore((s) => s.key);
  const hide = useToastStore((s) => s.hide);
  useEffect(() => {
    if (!msg) return;
    const t = setTimeout(hide, 2600);
    return () => clearTimeout(t);
  }, [msg, key, hide]);
  if (!msg) return null;
  return (
    <div className="toast" role="status" key={key}>
      {msg}
    </div>
  );
}

/** Small "lock" chip with sparks / reason. */
export function LockChip({ text }: { text: string }) {
  return (
    <span className="chip pill-gold">
      <Icon name="lock" size={14} /> {text}
    </span>
  );
}
